import React from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import * as styles from './RewardsInfoOnHover.css';

const cx = classNames.bind(styles);

export const RewardsInfoOnHover = (props) => {
    const { rewardsDetails, rewardsHoverInfoMessagesTexts, userName } = props;
    const {
        points: {
            dollarThreshold = 10,
            earnedPoints = 0,
            pointsAway = 200,
            pointsThreshold = 200,
        } = {},
        certificates: { totalCertsValue: currentRewards = 0, data = [] } = {},
    } = rewardsDetails;
    const expiryDates = data.map((certificate) => certificate.expiryDate);
    let expiryDate = (
        expiryDates.sort((value1, value2) => value1.localeCompare(value2))[0] || ''
    ).slice(0, -5);
    if (expiryDate.charAt(0) === '0') {
        expiryDate = expiryDate.slice(1);
    }
    const hideRewardsSection = isEmpty(rewardsDetails);
    const {
        startEarningText = [],
        learnMoreText,
        learnMoreLink,
        currentRewardsText = [],
        expiresText,
        rewardsPointsText,
        progressCircleStyles,
        pointsAwayText = [],
        newEarningsText = [],
        earnedPointThreshold = 50,
    } = rewardsHoverInfoMessagesTexts;
    const noCurrentRewardsAndPoints = currentRewards === 0 && earnedPoints === 0;
    const isCurrentRewardsPresent = currentRewards !== 0;
    const isPointsCircleRequired = earnedPoints !== 0 && earnedPoints >= earnedPointThreshold;

    const renderLearnMoreLink = () => {
        return (
            <div data-automation-id="rewards-learn-more-link">
                <a
                    className={cx(
                        'learnMoreLink',
                        'text-xsmall',
                        'font-open-sans-regular',
                        'text-gray-400',
                        'underline'
                    )}
                    href={learnMoreLink}
                >
                    {learnMoreText}
                </a>
            </div>
        );
    };

    const renderStartEarningText = () => {
        return (
            <div
                data-automation-id="rewards-start-earning-text"
                className="text-gray-600 text-center font-open-sans-regular text-small mt-1"
            >
                {startEarningText[0]}
                <span className="font-bold">{` $${dollarThreshold} ${startEarningText[1]} `}</span>
                {`${startEarningText[2]}${pointsThreshold}${startEarningText[3]}`}
                {renderLearnMoreLink()}
            </div>
        );
    };

    const renderCurrentRewardsText = () => {
        return (
            <div
                data-automation-id="rewards-current-certs-text"
                className="text-gray-600 text-medium font-open-sans-regular text-center mt-1"
            >
                <div>
                    {currentRewardsText[0]}{' '}
                    <span className="font-bold">{`$${currentRewards} ${currentRewardsText[1]}`}</span>
                </div>
                <div
                    data-automation-id="rewards-certs-expiry-text"
                    className="text-gray-400 font-open-sans-regular text-xsmall"
                >{`${expiresText} ${expiryDate}`}</div>
            </div>
        );
    };

    const renderPointsCircle = () => {
        const percentageOfRewards = Math.round((earnedPoints / pointsThreshold) * 100);
        return (
            <div className={cx('progressBar')}>
                <CircularProgressbarWithChildren
                    className={cx('progressCircle', 'font-open-sans-regular text-gray-400')}
                    value={percentageOfRewards}
                    styles={progressCircleStyles}
                >
                    <span className="text-large">
                        <span className="text-gray-600 font-bold">{earnedPoints}</span>/
                        {pointsThreshold}
                    </span>
                    <div className="text-xsmall">{rewardsPointsText}</div>
                </CircularProgressbarWithChildren>
            </div>
        );
    };

    const renderPointsAwayText = () => {
        const pointsAwayMessagesTexts = isCurrentRewardsPresent ? pointsAwayText : newEarningsText;
        let pointsAwayRenderText = `${pointsAwayMessagesTexts[0]} ${pointsAway} ${pointsAwayMessagesTexts[1]} $${dollarThreshold} ${pointsAwayMessagesTexts[2]}`;
        if (isCurrentRewardsPresent && !isPointsCircleRequired) {
            pointsAwayRenderText = `${pointsAway} ${pointsAwayText[1]} ${pointsAwayText[2]}`;
        }
        return (
            <div
                data-automation-id="rewards-points-away-text"
                className="text-small font-open-sans-regular text-gray-600 text-center m-auto"
            >
                {pointsAwayRenderText}
                {renderLearnMoreLink()}
            </div>
        );
    };
    return (
        <div
            data-automation-id="rewards-info-hover-container"
            className="px-3 py-2 bg-white border-b-1 border-solid border-gray-200"
        >
            <div
                data-automation-id="acc-hover-username"
                className="font-open-sans-regular text-gray-600 text-large text-center"
            >{`Hi, ${startCase(userName)}`}</div>
            {!hideRewardsSection && noCurrentRewardsAndPoints && renderStartEarningText()}
            {!hideRewardsSection && isCurrentRewardsPresent && renderCurrentRewardsText()}
            {!hideRewardsSection && (
                <div
                    data-automation-id="rewards-progress-hover-container"
                    className="flex flex-row justify-between mt-1"
                >
                    {isPointsCircleRequired && renderPointsCircle()}
                    {!noCurrentRewardsAndPoints && renderPointsAwayText()}
                </div>
            )}
        </div>
    );
};

RewardsInfoOnHover.defaultProps = {
    rewardsHoverInfoMessagesTexts: {},
    userName: '',
    rewardsDetails: {},
};

RewardsInfoOnHover.propTypes = {
    rewardsHoverInfoMessagesTexts: PropTypes.objectOf(PropTypes.any),
    userName: PropTypes.string,
    rewardsDetails: PropTypes.objectOf(PropTypes.any),
};

const mapStateToProps = (state) => {
    const rewardsHoverInfoMessagesTexts = _get(
        state,
        'context.messagesTexts.rewardsInfoOnHover',
        {}
    );
    return {
        rewardsHoverInfoMessagesTexts,
    };
};

export default connect(mapStateToProps, null)(RewardsInfoOnHover);
